import { useEffect, useState, useRef } from 'react';
import {
    Box, Typography, Slide, Fade, IconButton, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate } from 'react-router-dom';
import { ngWalletMenuItems } from '../constants/ngWalletMenuItems';

/* ── Brand tokens ── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    gold:    '#F5C842',
    orange:  '#FF9500',
    blue:    '#007AFF',
};

/* Section accent colours — cycles through brand palette */
const SECTION_COLORS = [B.gold, B.purple, B.green, B.orange, B.blue];

export default function MenuPage() {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [checked, setChecked]   = useState(false);
    const pendingRoute             = useRef<string | null>(null);

    const pageBg    = isDark ? B.deep    : B.lavender;
    const surf      = isDark ? alpha(B.mid, .55) : '#fff';
    const bdr       = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const textPri   = isDark ? '#fff'    : B.ink;
    const textMid   = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint = alpha(isDark ? '#fff' : B.ink, .28);

    useEffect(() => { setChecked(true); }, []);

    const handleExited = () => {
        if (pendingRoute.current) navigate(pendingRoute.current);
        else navigate(-1);
    };

    const handleBack = () => {
        setChecked(false);
        pendingRoute.current = null;
    };

    const handleNavigate = (link: string) => {
        setChecked(false);
        pendingRoute.current = link;
    };

    return (
        <>
            {/* Backdrop */}
            <Fade in={checked} timeout={250}>
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: alpha(B.deep, .6), backdropFilter: 'blur(4px)', zIndex: 1200 }} />
            </Fade>

            <Slide direction="right" in={checked} mountOnEnter unmountOnExit timeout={280} onExited={handleExited}>
                <Box sx={{
                    position: 'fixed', top: 0, left: 0,
                    width: { xs: '100%', sm: 360 },
                    height: '100vh',
                    background: isDark
                        ? `linear-gradient(180deg, ${B.dark} 0%, ${B.deep} 100%)`
                        : B.lavender,
                    zIndex: 1300,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: `8px 0 48px ${alpha(B.dark, .45)}`,
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                }}>

                    {/* ── Header ── */}
                    <Box sx={{
                        background: `linear-gradient(135deg, ${B.deep} 0%, ${B.mid} 60%, ${B.purple} 100%)`,
                        pt: `max(env(safe-area-inset-top), 52px)`,
                        pb: 3, px: 2.5,
                        position: 'relative', overflow: 'hidden', flexShrink: 0,
                    }}>
                        {/* Glow orb */}
                        <Box sx={{ position: 'absolute', top: -40, right: -30, width: 160, height: 160, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.purple, .25)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                        <Box display="flex" alignItems="center" gap={1.5}>
                            <IconButton onClick={handleBack} size="small" sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                                <ArrowBackOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Box>
                                <Typography sx={{ color: alpha('#fff', .4), fontSize: '.6rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', fontFamily: "'Instrument Serif', serif", letterSpacing: '-.02em' }}>Menu</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* ── Scrollable content ── */}
                    <Box sx={{
                        flex: 1, overflowY: 'auto', px: 2, py: 2.5,
                        pb: `calc(24px + env(safe-area-inset-bottom))`,
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                        {ngWalletMenuItems.map((section, sIdx) => {
                            const accent = SECTION_COLORS[sIdx % SECTION_COLORS.length];
                            return (
                                <Box key={sIdx} sx={{ mb: 3 }}>

                                    {/* Section label */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, px: .5 }}>
                                        <Box sx={{ width: 3, height: 14, borderRadius: 2, background: accent, flexShrink: 0 }} />
                                        <Typography sx={{ color: textFaint, fontSize: '.6rem', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                                            {section.title}
                                        </Typography>
                                    </Box>

                                    {/* Items card */}
                                    <Box sx={{
                                        background: surf,
                                        border: bdr,
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        boxShadow: `0 2px 16px ${alpha(B.dark, isDark ? .2 : .05)}`,
                                        backdropFilter: isDark ? 'blur(20px)' : 'none',
                                    }}>
                                        {section.items.map((item, iIdx) => {
                                            const Icon = item.icon;
                                            const isLast = iIdx === section.items.length - 1;
                                            return (
                                                <Box key={iIdx}>
                                                    <Box
                                                        onClick={() => handleNavigate(item.link)}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                                            px: 2, py: 1.4, cursor: 'pointer',
                                                            transition: 'background .15s',
                                                            '&:hover': { background: alpha(accent, isDark ? .08 : .05) },
                                                            '&:active': { background: alpha(accent, .12) },
                                                        }}
                                                    >
                                                        {/* Icon bubble */}
                                                        <Box sx={{
                                                            width: 36, height: 36, borderRadius: '11px', flexShrink: 0,
                                                            background: alpha(accent, isDark ? .14 : .09),
                                                            border: `1px solid ${alpha(accent, .18)}`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            <Icon size={16} color={accent} strokeWidth={2} />
                                                        </Box>

                                                        <Typography sx={{ color: textPri, fontSize: '.88rem', fontWeight: 500, flex: 1 }}>
                                                            {item.name}
                                                        </Typography>

                                                        {/* Chevron */}
                                                        <Box sx={{ color: textFaint, fontSize: '.7rem', lineHeight: 1 }}>›</Box>
                                                    </Box>

                                                    {!isLast && (
                                                        <Box sx={{ mx: 2, height: '1px', background: alpha(isDark ? '#fff' : B.dark, .06) }} />
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            );
                        })}

                        {/* Footer */}
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                            <Typography sx={{ fontSize: '.58rem', color: textFaint, letterSpacing: '.06em' }}>
                                256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Slide>
        </>
    );
}